import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../helpers/axios";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { useAiCreditsContext } from "../../../../context/AiCreditsContext";
import AiCreditConfirm from "../../../shared/AiCreditConfirm/AiCreditConfirm";
import "./BsiIntelligence.css";

/**
 * BSI Intelligence collapsible dropdown for Income Profile tab.
 * Shows hidden obligations, collection timing, alternate contacts, AI FOIR gauge.
 * Role-based: admin/crm = full, crsa/cra/institute_cra = partial (contacts masked), institute_admin/institute_user/collection = hidden.
 */
export default function BsiIntelligence({ userId, appId, source = "finbox", onAnalysisComplete }) {
  const decode = useCurrentUser();
  const { balance, deductCredits } = useAiCreditsContext();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bsiData, setBsiData] = useState(null);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [showFoirBreakdown, setShowFoirBreakdown] = useState(false);
  const [error, setError] = useState(null);

  // Role checks
  const role = decode?.role;
  const hasFullAccess = ["admin", "crm", "super_admin"].includes(role);
  const hasPartialAccess = ["crsa", "cra", "institute_cra"].includes(role);
  const hasNoAccess = ["institute_admin", "institute_user", "channel_user", "collection"].includes(role);

  useEffect(() => {
    if (!userId || !appId || hasNoAccess) return;
    fetchBsiData();
  }, [userId, appId]);

  const fetchBsiData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resultsRes, contactsRes] = await Promise.all([
        axiosInstance.get(`/bsi/results/${userId}/${appId}`),
        axiosInstance.get(`/bsi/contacts/${userId}/${appId}`),
      ]);
      setBsiData(resultsRes?.data?.data || resultsRes?.data || null);
      setContacts(contactsRes?.data?.data || contactsRes?.data || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setBsiData(null);
      } else {
        setError("Failed to load BSI data");
      }
    } finally {
      setLoading(false);
    }
  };

  // If no data available, show minimal collapsed state for first-time analysis
  if (hasNoAccess) return null;
  if (!loading && !bsiData && !error) {
    return (
      <div className={`bsi-dropdown`}>
        <div className="bsi-dropdown-header" onClick={() => setShowCreditConfirm(true)}>
          <div className="bsi-header-left">
            <h3 className="bsi-title">🧠 Bank Statement Intelligence</h3>
            <span className="bsi-ai-tag">AI Powered</span>
            <span style={{ fontSize: "11px", color: "#667085", marginLeft: "12px" }}>No analysis yet — click to run</span>
          </div>
          <div className="bsi-header-right">
            {hasFullAccess && <button className="bsi-refresh-btn" onClick={(e) => { e.stopPropagation(); setShowCreditConfirm(true); }}>▶ Run Analysis</button>}
          </div>
        </div>
        {hasFullAccess && (
          <AiCreditConfirm
            isOpen={showCreditConfirm}
            action="BSI First Analysis"
            credits={1}
            remainingCredits={balance?.remainingCredits}
            userName={decode?.display_name}
            onCancel={() => setShowCreditConfirm(false)}
            onConfirm={async () => {
              setShowCreditConfirm(false);
              setLoading(true);
              try {
                await axiosInstance.post(`/bsi/analyze/${source}/${userId}/${appId}`);
                await deductCredits({ action: "bsi_reanalyze", creditsUsed: 1, targetUserId: userId, targetApplicationId: appId });
                await fetchBsiData();
                onAnalysisComplete?.();
              } catch (err) {
                setError("Analysis failed. No credits were charged.");
              } finally {
                setLoading(false);
              }
            }}
          />
        )}
      </div>
    );
  }

  const obligations = bsiData?.hiddenObligations || [];
  const adjustedFoir = bsiData?.adjustedFoir || {};
  const collectionWindow = bsiData?.collectionWindow || {};
  const totalHidden = obligations.reduce((sum, o) => sum + (o.estimatedMonthlyAmount || 0), 0);
  const processedAt = bsiData?.processedAt ? new Date(bsiData.processedAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return "₹" + Number(amount).toLocaleString("en-IN");
  };

  return (
    <div className={`bsi-dropdown ${isOpen ? "bsi-open" : ""}`}>
      {/* Header - always visible */}
      <div className="bsi-dropdown-header" onClick={() => {
        const newState = !isOpen;
        setIsOpen(newState);
        // Auto-collapse inner sections when main dropdown collapses
        if (!newState) {
          setShowAllContacts(false);
          setShowFoirBreakdown(false);
        }
      }}>
        <div className="bsi-header-left">
          <h3 className="bsi-title">🧠 Bank Statement Intelligence</h3>
          <span className="bsi-ai-tag">AI Powered</span>
          {/* Quick summary chips when collapsed */}
          {!isOpen && bsiData && (
            <div className="bsi-quick-summary">
              {totalHidden > 0 && (
                <span className="bsi-quick-chip bsi-chip-warn">⚠️ Hidden EMIs: {formatCurrency(totalHidden)}/mo</span>
              )}
              {adjustedFoir?.adjustedFoir > 0 && (
                <span className="bsi-quick-chip bsi-chip-warn">Adjusted FOIR: {Math.round(adjustedFoir.adjustedFoir)}%</span>
              )}
              {contacts.length > 0 && (
                <span className="bsi-quick-chip bsi-chip-ok">✓ {contacts.length} Alternate Contacts</span>
              )}
            </div>
          )}
        </div>
        <div className="bsi-header-right">
          {bsiData && <div className="bsi-status"><div className="bsi-status-dot"></div> Cached</div>}
          {loading && <span style={{ fontSize: "11px", color: "#7c3aed" }}>Loading...</span>}
          <span className={`bsi-arrow ${isOpen ? "bsi-arrow-open" : ""}`}>▾</span>
        </div>
      </div>

      {/* Body - collapsible */}
      {isOpen && (
        <div className="bsi-dropdown-body">
          {loading && <div style={{ textAlign: "center", padding: "20px", color: "#7c3aed" }}>Loading BSI Intelligence...</div>}
          {error && <div style={{ textAlign: "center", padding: "20px", color: "#d32028" }}>{error}</div>}
          {bsiData && (
            <>
              <div className="bsi-subtitle">AI-extracted insights from UPI transactions & account patterns • PII anonymized before processing</div>

              <div className="bsi-cards">
                {/* Card 1: Alternate Contacts */}
                <div className="bsi-card">
                  <div className="bsi-card-icon">🔗</div>
                  <h4 className="bsi-card-title">
                    Alternate Borrower Contacts
                    {contacts.length > 0 && <span className="bsi-card-badge">{contacts.length} found</span>}
                  </h4>
                  <div className="bsi-card-desc">From frequent UPI transactions</div>
                  {hasPartialAccess ? (
                    <div className="bsi-masked-notice">Contact details masked for your role</div>
                  ) : contacts.length === 0 ? (
                    <div style={{ fontSize: "11px", color: "#667085", padding: "8px 0" }}>
                      No UPI contact numbers could be extracted — transaction data contains display names only, not VPA phone identifiers.
                    </div>
                  ) : (
                    <ul className="bsi-list">
                      {contacts.slice(0, 3).map((c, i) => (
                        <li key={i}>
                          <span>{c.relationshipClassification || "Unknown"}</span>
                          <span className={c.netFlowAmount >= 0 ? "bsi-green" : "bsi-highlight"}>
                            {c.counterpartyVpa || c.counterpartyPhone || `Contact ${i + 1}`}
                            <small style={{ color: "#667085", marginLeft: "4px" }}>({c.transactionCount} txns)</small>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {hasFullAccess && contacts.length > 3 && (
                    <div className="bsi-view-all" onClick={(e) => { e.stopPropagation(); setShowAllContacts(!showAllContacts); }}>
                      {showAllContacts ? "▾ Hide" : "▸ View all"} {contacts.length} contacts
                    </div>
                  )}
                </div>

                {/* Card 2: Collection Timing */}
                <div className="bsi-card">
                  <div className="bsi-card-icon">⏰</div>
                  <h4 className="bsi-card-title">Optimal Collection Windows</h4>
                  <div className="bsi-card-desc">Based on salary credit & balance patterns</div>
                  <ul className="bsi-list">
                    <li><span>Salary Credit Day</span><span className="bsi-green">{collectionWindow.salaryDay ? `${collectionWindow.salaryDay}th` : "—"}</span></li>
                    <li><span>Best EMI Date</span><span className="bsi-green">{collectionWindow.recommendedDay ? `${collectionWindow.recommendedDay}th` : "—"}</span></li>
                    <li><span>Peak Balance</span><span className="bsi-green">{collectionWindow.peakBalanceDay ? `${collectionWindow.peakBalanceDay}th - ${collectionWindow.peakBalanceDay + 5}th` : "—"}</span></li>
                    <li><span>Avoid</span><span className="bsi-highlight">{collectionWindow.avoidanceDays?.length > 0 ? collectionWindow.avoidanceDays.join(", ") : "—"}</span></li>
                  </ul>
                </div>

                {/* Card 3: Hidden Obligations */}
                <div className="bsi-card">
                  <div className="bsi-card-icon">🔍</div>
                  <h4 className="bsi-card-title">Hidden Obligations</h4>
                  <div className="bsi-card-desc">Undisclosed loans from transaction patterns</div>
                  {obligations.length === 0 ? (
                    <div style={{ fontSize: "11px", color: "#059669", padding: "8px 0" }}>✓ No hidden obligations detected</div>
                  ) : (
                    <ul className="bsi-list">
                      {obligations.map((o, i) => (
                        <li key={i}>
                          <span>{o.lenderIdentifier} <small style={{ color: "#667085" }}>{o.type === "fintech_loan" ? "Fintech" : "P2P"} • {o.monthsDetected}mo</small></span>
                          <span className="bsi-highlight">{formatCurrency(o.estimatedMonthlyAmount)} <small style={{ color: "#059669" }}>{Math.round(o.detectionConfidence * 100)}%</small></span>
                        </li>
                      ))}
                      <li>
                        <span><strong>Total Hidden</strong></span>
                        <span className="bsi-highlight"><strong>{formatCurrency(totalHidden)}/mo</strong></span>
                      </li>
                    </ul>
                  )}
                </div>
              </div>

              {/* All Contacts Expanded */}
              {showAllContacts && hasFullAccess && contacts.length > 0 && (
                <div className="bsi-all-contacts">
                  <div className="bsi-all-contacts-title">All Alternate Contacts ({contacts.length})</div>
                  <table className="bsi-contacts-table">
                    <thead>
                      <tr>
                        <th>Contact</th>
                        <th>Relationship</th>
                        <th style={{ textAlign: "center" }}>Txns</th>
                        <th style={{ textAlign: "right" }}>Net Flow</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c, i) => (
                        <tr key={i}>
                          <td>{c.counterpartyVpa || c.counterpartyPhone || c.counterpartyName || `Contact ${i + 1}`}</td>
                          <td>{c.relationshipClassification || "Unknown"}</td>
                          <td style={{ textAlign: "center" }}>{c.transactionCount}</td>
                          <td style={{ textAlign: "right", color: c.netFlowAmount >= 0 ? "#059669" : "#d32028" }}>
                            {c.netFlowAmount >= 0 ? "+" : ""}{formatCurrency(c.netFlowAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* FOIR Comparison */}
              {adjustedFoir?.adjustedFoir > 0 && (
                <div className="bsi-foir-compare">
                  <div className="bsi-foir-bars">
                    <div className="bsi-foir-bar-item">
                      <div className="bsi-foir-bar-labels">
                        <span>Standard FOIR</span>
                        <span style={{ fontWeight: 700, color: "#3b82f6" }}>{Math.round(adjustedFoir.standardFoir || 0)}%</span>
                      </div>
                      <div className="bsi-foir-bar-track">
                        <div className="bsi-foir-bar-fill bsi-foir-standard" style={{ width: `${Math.min(adjustedFoir.standardFoir || 0, 100)}%` }}></div>
                      </div>
                    </div>
                    <div className="bsi-foir-bar-item">
                      <div className="bsi-foir-bar-labels">
                        <span>AI FOIR</span>
                        <span style={{ fontWeight: 700, color: "#d32028" }}>{Math.round(adjustedFoir.adjustedFoir)}%</span>
                      </div>
                      <div className="bsi-foir-bar-track">
                        <div className="bsi-foir-bar-fill bsi-foir-adjusted" style={{ width: `${Math.min(adjustedFoir.adjustedFoir, 100)}%` }}></div>
                      </div>
                    </div>
                    {adjustedFoir.foirDelta > 0 && (
                      <span className="bsi-foir-delta">▲ +{Math.round(adjustedFoir.foirDelta)}%</span>
                    )}
                  </div>
                  {totalHidden > 0 && (
                    <div className="bsi-foir-warning">
                      Hidden EMIs {formatCurrency(totalHidden)}/mo. Declared {formatCurrency(adjustedFoir.bureauReportedEmis)} → Actual {formatCurrency((adjustedFoir.bureauReportedEmis || 0) + totalHidden)}.
                      <span className="bsi-foir-breakdown-toggle" onClick={(e) => { e.stopPropagation(); setShowFoirBreakdown(!showFoirBreakdown); }}>
                        {showFoirBreakdown ? "▾ Hide breakdown" : "▸ How is this calculated?"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* FOIR Breakdown */}
              {showFoirBreakdown && adjustedFoir?.adjustedFoir > 0 && (
                <div className="bsi-foir-breakdown">
                  <div className="bsi-foir-breakdown-title">FOIR Calculation Breakdown</div>
                  <table className="bsi-breakdown-table">
                    <tbody>
                      <tr className="bsi-breakdown-header-row">
                        <td>Income (Monthly)</td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>{formatCurrency(adjustedFoir.averageMonthlyIncome)}</td>
                      </tr>
                      <tr className="bsi-breakdown-section">
                        <td colSpan="2">Declared Obligations (from CIBIL + Application)</td>
                      </tr>
                      <tr>
                        <td className="bsi-breakdown-indent">Bureau reported EMIs</td>
                        <td style={{ textAlign: "right" }}>{formatCurrency(adjustedFoir.bureauReportedEmis)}</td>
                      </tr>
                      <tr className="bsi-breakdown-result">
                        <td>Standard FOIR</td>
                        <td style={{ textAlign: "right", color: "#3b82f6" }}>{Math.round(adjustedFoir.standardFoir || 0)}%</td>
                      </tr>
                      <tr className="bsi-breakdown-section bsi-breakdown-hidden">
                        <td colSpan="2">+ Hidden Obligations (AI-detected from bank statement)</td>
                      </tr>
                      {obligations.map((o, i) => (
                        <tr key={i}>
                          <td className="bsi-breakdown-indent" style={{ color: "#991b1b" }}>{o.lenderIdentifier} ({o.type === "fintech_loan" ? "Fintech" : "P2P"})</td>
                          <td style={{ textAlign: "right", color: "#d32028" }}>+ {formatCurrency(o.estimatedMonthlyAmount)}</td>
                        </tr>
                      ))}
                      <tr className="bsi-breakdown-hidden-total">
                        <td className="bsi-breakdown-indent">Total Hidden</td>
                        <td style={{ textAlign: "right", color: "#d32028", fontWeight: 600 }}>{formatCurrency(totalHidden)}</td>
                      </tr>
                      <tr className="bsi-breakdown-final">
                        <td>AI FOIR</td>
                        <td style={{ textAlign: "right", color: "#d32028", fontWeight: 700 }}>{Math.round(adjustedFoir.adjustedFoir)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer */}
              <div className="bsi-footer">
                <div className="bsi-cache-info">
                  <div className="bsi-cache-dot"></div>
                  {processedAt ? `Analyzed on ${processedAt}` : "Analysis available"}
                </div>
                {hasFullAccess && (
                  <div>
                    <button className="bsi-refresh-btn" onClick={(e) => { e.stopPropagation(); setShowCreditConfirm(true); }}>↻ Re-analyze</button>
                    <span className="bsi-credit-info">⚡ 1 AI credit</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <AiCreditConfirm
        isOpen={showCreditConfirm}
        action="BSI Re-analyze"
        credits={1}
        remainingCredits={balance?.remainingCredits}
        userName={decode?.display_name}
        onCancel={() => setShowCreditConfirm(false)}
        onConfirm={async () => {
          setShowCreditConfirm(false);
          // First trigger re-analysis, then deduct credit only on success
          setLoading(true);
          try {
            await axiosInstance.post(`/bsi/analyze/${source}/${userId}/${appId}`);
            const deductResult = await deductCredits({ action: "bsi_reanalyze", creditsUsed: 1, targetUserId: userId, targetApplicationId: appId });
            if (!deductResult.success) {
              setError("Analysis triggered but credit deduction failed. Contact admin.");
            }
            await fetchBsiData();
            onAnalysisComplete?.();
          } catch (err) {
            setError("Re-analysis failed. No credits were charged.");
          } finally {
            setLoading(false);
          }
        }}
      />
    </div>
  );
}
